import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';

const StudentTimetable: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [timetable, setTimetable] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = [
    { label: '8:00 AM', value: '08:00' },
    { label: '9:00 AM', value: '09:00' },
    { label: '10:00 AM', value: '10:00' },
    { label: '11:00 AM', value: '11:00' },
    { label: '12:00 PM', value: '12:00' },
    { label: '1:00 PM', value: '13:00' }
  ];

  useEffect(() => {
    fetchTimetable();
  }, []);

  const fetchTimetable = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/timetable/student');
      setTimetable(response.data.timetable || []);
    } catch (error) {
      console.error('Error fetching timetable:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClassForSlot = (day: string, timeSlot: string) => {
    return timetable.find(
      entry => entry.dayOfWeek === day && entry.startTime?.substring(0, 5) === timeSlot
    );
  };

  if (loading) {
    return (
      <Layout title="My Timetable">
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Loading timetable...
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="My Timetable">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Weekly Class Schedule
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-gray-100 text-gray-900'}`}>
                  Time
                </th>
                {days.map(day => (
                  <th
                    key={day}
                    className={`border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-gray-100 text-gray-900'}`}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(slot => (
                <tr key={slot.value}>
                  <td className={`border p-3 font-medium text-center ${isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-gray-50 text-gray-700'}`}>
                    {slot.label}
                  </td>
                  {days.map(day => {
                    const classData = getClassForSlot(day, slot.value);
                    return (
                      <td
                        key={`${day}-${slot.value}`}
                        className={`border p-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}
                      >
                        {classData ? (
                          <div className={`p-3 rounded ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                            <p className={`font-semibold ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                              {classData.subject?.name || 'N/A'}
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                              {classData.teacher ? `${classData.teacher.user?.firstName} ${classData.teacher.user?.lastName}` : 'TBA'}
                            </p>
                            {classData.room && (
                              <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                Room: {classData.room}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className={`text-center text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                            -
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {timetable.length === 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No timetable entries found. Please contact your administrator.
          </div>
        )}

        <div className="mt-6">
          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Legend
          </h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <div className={`w-4 h-4 rounded mr-2 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`}></div>
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Regular Class</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentTimetable;
